import { PkIcon } from '@uefadigital/panenka-react';
import type { ICellRendererParams } from 'ag-grid-community';
import clsx from 'clsx';
import type { HTMLAttributeAnchorTarget, MouseEvent } from 'react';
import { memo, useMemo } from 'react';

import TeamIdentifier from '@shared/Identifier/TeamIdentifier';
import useMatch from '@shared/MatchUnit/useMatch';
import QualifiedIndicator from '@shared/QualifiedIndicator';
import { useTranslateFn } from '@shared/Translate';
import type { Match, Team } from '@shared-types/match.types';
import type { Standing } from '@shared-types/standing.types';
import { StandingTrend } from '@shared-types/standing.types';
import { matchScore } from '@utilities/live';

import * as classes from './styles.module.scss';

const LiveIndicator = ({ team }: { team: Team & { nextMatch: Match } }) => {
  const match = useMatch(team.nextMatch);
  const t = useTranslateFn();

  const data = useMemo(() => {
    if (!match.score) return null;

    const homeScore = matchScore(match, 'home');
    const awayScore = matchScore(match, 'away');

    const teamScore = team.id === match.homeTeam.id ? homeScore : awayScore;
    const opponentScore = team.id === match.homeTeam.id ? awayScore : homeScore;

    let status = 'draw';
    if (homeScore !== awayScore) {
      // the team is home
      if (team.id === match.homeTeam.id) {
        status = homeScore > awayScore ? 'win' : 'lose';
      }

      // the team is away
      else status = homeScore < awayScore ? 'win' : 'lose';
    }

    return { status, teamScore, opponentScore };
  }, [match, team.id]);

  if (!data) return null;

  const { status, teamScore, opponentScore } = data;

  return (
    <>
      {t('live_score')}
      {': '}
      <span className={clsx(classes.score, status && classes[`score--${status}`])}>
        {teamScore ?? 0}-{opponentScore ?? 0}
      </span>
    </>
  );
};

const trendMapping = {
  [StandingTrend.EQUAL]: {
    icon: 'actions-equal',
    color: 'ui-04',
  },
  [StandingTrend.UP]: {
    icon: 'football-substitution-in',
    color: 'support-01',
  },
  [StandingTrend.DOWN]: {
    icon: 'football-substitution-out',
    color: 'support-04',
  },
} as const;

const TeamCellRender = ({
  value: team,
  data,
  onClick,
  hideQualified = false,
  showRank = true,
  showTrend = true,
  enableLink = true,
  linkTarget = '_self',
}: ICellRendererParams<Standing, Team & { nextMatch: Match }> & {
  onClick?: (e: MouseEvent) => void;
  hideQualified?: boolean;
  showRank?: boolean;
  showTrend?: boolean;
  /** Enable link URL in the cell content */
  enableLink?: boolean;
  /** Where to display the linked URL */
  linkTarget?: HTMLAttributeAnchorTarget;
}) => {
  const secondaryText = useMemo(
    () => (team.nextMatch && data.isLive ? <LiveIndicator team={team} /> : null),
    [data.isLive, team],
  );

  const trend = useMemo(() => trendMapping[data.rankTrend], [data.rankTrend]);
  const qualified = !hideQualified && (data.qualified || !!data.qualificationLabelId);

  return (
    <>
      <QualifiedIndicator
        labelId={data.qualificationLabelId}
        type='bar'
        className={clsx(!qualified && 'pk-visibility--invisible', !showRank && 'pk-mr--xs2')}
      />
      {showRank && (
        <div className={clsx(classes.rank, 'pk-d--flex pk-justify-content--between pk-align-items--center pk-mx--xs2')}>
          <span className='pk-text--text-03'>{data.rank}</span>
          {showTrend && trend && <PkIcon name={trend.icon} color={trend.color} size={11} title={data.rankTrend} />}
        </div>
      )}
      <TeamIdentifier
        className={classes.identifier}
        wrapperClass={classes.identifierWrapper}
        badgeSize={32}
        showTeamCountry={false}
        team={team}
        enableLink={enableLink}
        linkTarget={linkTarget}
        onLinkClick={onClick}
        customSecondaryText={secondaryText}
      />
    </>
  );
};

export default memo(TeamCellRender);
